import React, { useState, useEffect, useCallback } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Search, MessageCircle, ArrowLeft, Plus, BadgeCheck, Shield, Lock, AlertCircle, RefreshCw, ShieldCheck, Key, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

// Import E2E encryption utilities
import {
  isE2EAvailable,
  initializeE2E,
  sendEncryptedMessage,
  readEncryptedMessage,
  getStoredKeyPair
} from '@/utils/encryption';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Messages = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  
  // E2E encryption state
  const [e2eInitialized, setE2eInitialized] = useState(false);
  const [e2eStatus, setE2eStatus] = useState(null);
  const [encryptionEnabled, setEncryptionEnabled] = useState(true);
  const [showEncryptionInfo, setShowEncryptionInfo] = useState(false);
  const [recipientPublicKey, setRecipientPublicKey] = useState(null);
  const [decryptedMessages, setDecryptedMessages] = useState({});

  // Initialize E2E encryption
  const initE2E = useCallback(async () => {
    if (!user?.user_id) return;
    
    try {
      const status = await initializeE2E(user.user_id);
      setE2eStatus(status);
      setE2eInitialized(status.initialized);
      
      if (status.initialized && status.publicKey) {
        // Register public key with server
        try {
          await axios.post(`${API_URL}/messages/register-public-key`, {
            public_key: JSON.stringify(status.publicKey)
          }, { withCredentials: true });
        } catch (err) {
          console.log('Public key registration:', err.response?.data?.detail || 'Already registered');
        }
      }
    } catch (error) {
      console.error('E2E init error:', error);
      setE2eInitialized(false);
    }
  }, [user?.user_id]);
  
  // Fetch recipient's public key
  const fetchRecipientPublicKey = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/messages/public-key/${userId}`, { withCredentials: true });
      if (response.data.public_key) {
        setRecipientPublicKey(JSON.parse(response.data.public_key));
      }
    } catch (error) {
      console.log('Recipient public key not available');
    }
  };
  
  // Decrypt a message
  const decryptMessageContent = async (message) => {
    if (!e2eInitialized || !message.is_encrypted) return message.content;
    
    try {
      const encryptedPackage = message.sender_id === user.user_id 
        ? message.encrypted_for_sender 
        : message.encrypted_for_recipient;
        
      if (encryptedPackage) {
        const decrypted = await readEncryptedMessage(user.user_id, encryptedPackage);
        return decrypted;
      }
    } catch (error) {
      console.log('Decryption failed:', error);
    }
    return message.content || '[Encrypted message]';
  };

  useEffect(() => {
    fetchConversations();
    if (isE2EAvailable) {
      initE2E();
    }
  }, [initE2E]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversations`, {
        withCredentials: true,
      });
      setConversations(response.data);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    setDecryptedMessages({});
    
    // Fetch recipient's public key for encryption
    if (conv.other_user?.user_id) {
      fetchRecipientPublicKey(conv.other_user.user_id);
    }
    
    try {
      const response = await axios.get(`${API_URL}/messages/conversation/${conv.conversation_id}`, {
        withCredentials: true,
      });
      setMessages(response.data);
      
      // Decrypt messages in background
      if (e2eInitialized) {
        const decrypted = {};
        for (const msg of response.data) {
          if (msg.is_encrypted) {
            decrypted[msg.message_id] = await decryptMessageContent(msg);
          }
        }
        setDecryptedMessages(decrypted);
      }
      
      await axios.post(`${API_URL}/messages/conversation/${conv.conversation_id}/read`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    
    try {
      let messagePayload = {
        recipient_id: selectedConv.other_user.user_id,
        content: newMessage,
      };
      
      // Encrypt message if E2E is available and recipient has public key
      if (e2eInitialized && encryptionEnabled && recipientPublicKey) {
        try {
          const encryptedData = await sendEncryptedMessage(
            user.user_id,
            recipientPublicKey,
            newMessage
          );
          messagePayload = {
            ...messagePayload,
            is_encrypted: true,
            encrypted_for_recipient: encryptedData.forRecipient,
            encrypted_for_sender: encryptedData.forSender,
          };
        } catch (encError) {
          console.log('Encryption failed, sending plaintext:', encError);
        }
      }
      
      const response = await axios.post(
        `${API_URL}/messages/send`,
        messagePayload,
        { withCredentials: true }
      );
      
      // Add to messages and decrypt for display
      const newMsg = response.data;
      setMessages([...messages, newMsg]);
      
      if (newMsg.is_encrypted) {
        setDecryptedMessages(prev => ({
          ...prev,
          [newMsg.message_id]: newMessage
        }));
      }
      
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/users/search?q=${query}`, { withCredentials: true });
      setSearchResults(response.data.filter(u => u.user_id !== user.user_id));
    } catch (error) {
      console.log('Search failed');
    }
  };

  const startNewConversation = async (targetUser) => {
    // Check if conversation already exists
    const existing = conversations.find(c => c.other_user?.user_id === targetUser.user_id);
    if (existing) {
      setSelectedConv(existing);
      setShowNewChat(false);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    
    // Create new conversation object for UI
    const newConv = {
      conversation_id: `temp_${Date.now()}`,
      other_user: targetUser,
      last_message: '',
      unread_count: 0
    };
    setSelectedConv(newConv);
    setMessages([]);
    setShowNewChat(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="messages-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-heading font-black text-gray-900 dark:text-white" data-testid="messages-title">
                Messages
              </h1>
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Lock className="w-3 h-3" />
                <span>End-to-End Encrypted</span>
              </div>
            </div>
          </div>
          <Button
            onClick={() => setShowNewChat(true)}
            className="rounded-full bg-primary hover:bg-primary-700 text-white"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Conversations List or New Chat Search */}
          <Card className="md:col-span-1 p-4 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md max-h-[600px] overflow-hidden flex flex-col" data-testid="conversations-list">
            {showNewChat ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={() => setShowNewChat(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h3 className="font-bold text-gray-900 dark:text-white">New Chat</h3>
                </div>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search people..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="pl-10 rounded-xl"
                    data-testid="search-users-input"
                  />
                </div>
                <div className="flex-1 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">
                      {searchQuery ? 'No users found' : 'Search for someone to chat with'}
                    </p>
                  ) : (
                    searchResults.map((u) => (
                      <div
                        key={u.user_id}
                        onClick={() => startNewConversation(u)}
                        className="p-3 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mb-2"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={u.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                              {u.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-1">
                              <p className="font-semibold text-gray-900 dark:text-white text-sm">{u.name}</p>
                              {u.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                              {u.is_admin && <Shield className="w-4 h-4 text-red-500" />}
                            </div>
                            <p className="text-xs text-gray-500">@{u.username}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search conversations..."
                      className="pl-10 rounded-xl"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400 mb-2">No conversations yet</p>
                      <Button
                        size="sm"
                        onClick={() => setShowNewChat(true)}
                        className="rounded-full"
                      >
                        Start a Chat
                      </Button>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <div
                        key={conv.conversation_id}
                        onClick={() => selectConversation(conv)}
                        className={`p-4 rounded-2xl cursor-pointer transition-all mb-2 ${
                          selectedConv?.conversation_id === conv.conversation_id
                            ? 'bg-primary text-white'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        data-testid={`conversation-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={conv.other_user?.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                              {conv.other_user?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <h3 className="font-bold truncate">{conv.other_user?.name}</h3>
                              {conv.other_user?.is_verified && (
                                <BadgeCheck className={`w-4 h-4 ${selectedConv?.conversation_id === conv.conversation_id ? 'text-white' : 'text-blue-500'}`} />
                              )}
                            </div>
                            <p className="text-sm truncate opacity-75">{conv.last_message}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <div className="bg-accent text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                              {conv.unread_count}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </Card>

          <Card className="md:col-span-2 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md overflow-hidden" data-testid="messages-panel">
            {!selectedConv ? (
              <div className="flex flex-col items-center justify-center h-[600px] text-gray-500 p-6">
                <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Your Messages</h3>
                <p className="text-center text-gray-500 mb-2">Send private messages to friends</p>
                <div className="flex items-center gap-1 text-sm text-green-600 mb-4">
                  <Lock className="w-4 h-4" />
                  <span>All messages are end-to-end encrypted</span>
                </div>
                <Button onClick={() => setShowNewChat(true)} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Start New Chat
                </Button>
              </div>
            ) : (
              <div className="flex flex-col h-[600px]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={selectedConv.other_user?.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                        {selectedConv.other_user?.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <h3 className="font-bold text-gray-900 dark:text-white">{selectedConv.other_user?.name}</h3>
                        {selectedConv.other_user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                        {selectedConv.other_user?.is_admin && <Shield className="w-4 h-4 text-red-500" />}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">@{selectedConv.other_user?.username}</p>
                    </div>
                    <Link to={`/profile/${selectedConv.other_user?.user_id}`}>
                      <Button variant="ghost" size="sm" className="rounded-full">
                        View Profile
                      </Button>
                    </Link>
                    
                    {/* Encryption Status Indicator */}
                    <div className="relative">
                      <button
                        onClick={() => setShowEncryptionInfo(!showEncryptionInfo)}
                        className={`p-2 rounded-full transition-colors ${
                          e2eInitialized 
                            ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' 
                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        data-testid="encryption-info-btn"
                      >
                        <ShieldCheck className="w-5 h-5" />
                      </button>
                      
                      {showEncryptionInfo && (
                        <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-10">
                          <div className="flex items-center gap-2 mb-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              e2eInitialized ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                            }`}>
                              <Lock className={`w-4 h-4 ${e2eInitialized ? 'text-green-600' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white text-sm">
                                {e2eInitialized ? 'End-to-End Encrypted' : 'Encryption Pending'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {e2eInitialized ? 'Only you and the recipient can read' : 'Setting up secure connection'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <Key className="w-3 h-3" />
                              <span>Algorithm: RSA-OAEP + AES-GCM-256</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="w-3 h-3" />
                              <span>Keys stored on your device only</span>
                            </div>
                            {recipientPublicKey && (
                              <div className="flex items-center gap-2 text-green-600">
                                <BadgeCheck className="w-3 h-3" />
                                <span>Recipient verified</span>
                              </div>
                            )}
                          </div>
                          
                          <button
                            onClick={() => setShowEncryptionInfo(false)}
                            className="mt-3 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <p className="text-sm">No messages yet. Say hi! 👋</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isOwn = msg.sender_id === user.user_id;
                      const displayContent = msg.is_encrypted 
                        ? (decryptedMessages[msg.message_id] || msg.content || '🔒 Encrypted')
                        : msg.content;
                      return (
                        <div
                          key={msg.message_id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs sm:max-w-md rounded-2xl px-4 py-2 ${
                              isOwn
                                ? 'bg-gradient-to-br from-primary to-accent text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                            }`}
                          >
                            <p>{displayContent}</p>
                            <div className={`flex items-center gap-1 text-xs ${
                              isOwn ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {msg.is_encrypted && <Lock className="w-3 h-3" />}
                              <span>{formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="rounded-xl border-2"
                      data-testid="message-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="rounded-full bg-primary hover:bg-primary-700 text-white px-6 shadow-lg disabled:opacity-50"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Messages;